import { useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, Dialog } from '@mui/material';
import FormErrors from '../../components/FormErrors';
import { signUp } from '../../lib/allauth';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../auth';
import ProviderList from '../../socialaccount/ProviderList';
import BackgroundContainer from '../../components/BackgroundContainer';
import BaseHeader from '../../components/BaseHeader';
import BaseButton from '../../components/BaseButton';
import BaseTextField from '../../components/BaseTextField';
import BaseFormContainer from '../../components/BaseFormContainer';
import TermsOfUseContent from '../../components/TermsOfUseContent';
import PrivacyPolicyContent from '../../components/PrivacyPolicyContent';


export default function Signup() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ageError, setAgeError] = useState('');
  const [isEligible, setIsEligible] = useState(false);
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [response, setResponse] = useState({ fetching: false, content: null });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [showLegalPage, setShowLegalPage] = useState(null) // 'terms' or 'privacy' or null
  const [emailErrors, setEmailErrors] = useState([]);
  const [password1Errors, setPassword1Errors] = useState([]);
  const [password2Errors, setPassword2Errors] = useState([]);


  const navigate = useNavigate()
  const config = useConfig();
  const hasProviders = config.data.socialaccount?.providers?.length > 0;

  const handleDateChange = (e) => {
    const dob = new Date(e.target.value);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const isBirthdayPassed = today.getMonth() > dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    const calculatedAge = isBirthdayPassed ? age : age - 1;

    setDateOfBirth(e.target.value);
    if (calculatedAge < 18) {
      setAgeError('You must be at least 18 years old to sign up.');
      setIsEligible(false);
    } else {
      setAgeError('');
      setIsEligible(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEligible) {
      alert('You must be at least 18 years old to sign up.');
      return;
    }
    if (password1 !== password2) {
      setPassword2Errors([{ param: 'password2', message: 'Passwords do not match.' }]);
      return;
    }
    setPassword2Errors([]);
    setResponse({ ...response, fetching: true });

    signUp({
      email,
      name,
      date_of_birth: dateOfBirth,
      password: password1,
      marketing_consent: marketingConsent,
      acquisition_source: sessionStorage.getItem('utm_source') || '',
      acquisition_campaign: sessionStorage.getItem('utm_campaign') || '',
    })
    .then((content) => {
      console.log('Signup response:', content);
      if (content?.errors?.length) {
        setEmailErrors(content.errors.filter(err => err.param === 'email'));
        setPassword1Errors(content.errors.filter(err => err.param === 'password'));
        return;
      }
      setResponse((prev) => ({ ...prev, content }));
    })
    .catch((error) => {
      console.error("Unexpected error (network or bug):", error);
      window.alert('An unexpected error occurred.');
    })
      .finally(() => setResponse((prev) => ({ ...prev, fetching: false })));
  };

  return (
    <BackgroundContainer sx={{
      justifyContent: 'flex-start',
      overflowY: 'auto',
    }}>
      <BaseFormContainer sx={{
        width: '100%',
        padding: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
        margin: 'auto',
      }} >
        <BaseHeader sx={{
          fontSize: '4rem', // 🎯 Full size title
          '@media (max-width: 360px)': {
            fontSize: '3.8rem', // 🧠 Gentle scale-down on ultra-small screens
          },
          '@media (max-width: 320px)': {
            fontSize: '3rem', // 🔒 Last-resort fallback for smallest devices
            lineHeight: '1.1',
          },
          textAlign: 'center', // (optional) center if you want
        }} >Sign Up</BaseHeader>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', }}>
          <BaseTextField
            sx={{
              width: '100%',
              maxWidth: '250px',
              marginBottom: '10px', // 📏 Looks good on larger screens
              fontSize: '1.1rem',
              '& .MuiInputBase-input': {
                padding: '10px',
                fontSize: '1.1rem',
                '@media (max-width: 360px)': {
                  padding: '10px',
                  fontSize: '1rem', // 🧠 Slightly smaller on small phones
                },
                '@media (max-width: 320px)': {
                  padding: '8px',
                  fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
                },
              },
            }}
            label="Email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailErrors([]); }}
            type="email"
            required
          />
          <FormErrors errors={emailErrors} param="email" />
          <BaseTextField
            sx={{
              width: '100%',
              maxWidth: '250px',
              marginBottom: '10px', // 📏 Looks good on larger screens
              fontSize: '1.1rem',
              '& .MuiInputBase-input': {
                padding: '10px',
                fontSize: '1.1rem',
                '@media (max-width: 360px)': {
                  padding: '10px',
                  fontSize: '1rem', // 🧠 Slightly smaller on small phones
                },
                '@media (max-width: 320px)': {
                  padding: '8px',
                  fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
                },
              },
            }}
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            required
          />
          <BaseTextField
            sx={{
              width: '100%',
              maxWidth: '250px',
              marginBottom: '10px',
              fontSize: '1.1rem',
              '& .MuiInputBase-input': {
                padding: '10px',
                fontSize: '1.1rem',
                colorScheme: 'dark',
                color: dateOfBirth ? 'inherit' : 'transparent',
                '&:focus': { color: 'inherit' },
                '@media (max-width: 360px)': {
                  padding: '10px',
                  fontSize: '1rem',
                },
                '@media (max-width: 320px)': {
                  padding: '8px',
                  fontSize: '0.95rem',
                },
              },
            }}
            label="Date of Birth"
            InputLabelProps={{ shrink: !!dateOfBirth }}
            type="date"
            value={dateOfBirth}
            onChange={handleDateChange}
            required
          />
          {ageError &&
            <Typography
              sx={{ paddingLeft: '8px', color: '#ff8899', fontSize: '0.9rem', marginBottom: '10px' }}
            >
              {ageError}
            </Typography>
          }
          <BaseTextField
            sx={{
              width: '100%',
              maxWidth: '250px',
              marginBottom: '10px', // 📏 Looks good on larger screens
              fontSize: '1.1rem',
              '& .MuiInputBase-input': {
                padding: '10px',
                fontSize: '1.1rem',
                '@media (max-width: 360px)': {
                  padding: '10px',
                  fontSize: '1rem', // 🧠 Slightly smaller on small phones
                },
                '@media (max-width: 320px)': {
                  padding: '8px',
                  fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
                },
              },
            }}
            label="Password"
            value={password1}
            onChange={(e) => {
              setPassword1(e.target.value);
              setPassword1Errors([]);
            }}
            type="password"
            required
          />
          <FormErrors errors={password1Errors} param="password" />

          <BaseTextField
            sx={{
              width: '100%',
              maxWidth: '250px',
              marginBottom: '10px', // 📏 Looks good on larger screens
              fontSize: '1.1rem',
              '& .MuiInputBase-input': {
                padding: '10px',
                fontSize: '1.1rem',
                '@media (max-width: 360px)': {
                  padding: '10px',
                  fontSize: '1rem', // 🧠 Slightly smaller on small phones
                },
                '@media (max-width: 320px)': {
                  padding: '8px',
                  fontSize: '0.95rem', // 🧬 Tighter on very narrow phones
                },
              },
            }}
            label="Confirm Password"
            value={password2}
            onChange={(e) => {
              setPassword2(e.target.value);
              setPassword2Errors([]);
            }}
            type="password"
            required
          />
          <FormErrors errors={password2Errors} param="password2" />
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                sx={{
                  color: '#ffbfe5',
                  '&.Mui-checked': {
                    color: '#ffbfe5',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.9rem', color: '#c2b8c2' }}>
                I agree to the{' '}
                <Box component="span" sx={{ color: '#ffbfe5', cursor: 'pointer' }} onClick={() => setShowLegalPage('terms')}>
                  Terms of Use
                </Box>{' '}
                and{' '}
                <Box component="span" sx={{ color: '#ffbfe5', cursor: 'pointer' }} onClick={() => setShowLegalPage('privacy')}>
                  Privacy Policy
                </Box>
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                sx={{
                  color: '#ffbfe5',
                  '&.Mui-checked': {
                    color: '#ffbfe5',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.9rem', color: '#c2b8c2' }}>
                I'd like to receive updates, tips, and offers from Deeper
              </Typography>
            }
          />
          <Dialog
            open={!!showLegalPage}
            onClose={() => setShowLegalPage(null)}
            fullScreen
            PaperProps={{
              style: {
                background: '#0C0717',
                padding: '20px',
                color: '#fff',
              }
            }}
          >
            <Box sx={{ overflowY: 'auto', maxHeight: '80vh', padding: '10px' }}>
              {showLegalPage === 'terms' ? <TermsOfUseContent /> : <PrivacyPolicyContent />}
            </Box>
            
            <BaseButton onClick={() => setShowLegalPage(null)} sx={{
            width: '100%',
            marginTop: '10px',
            marginBottom: '20px',
            alignSelf: 'center',
            fontSize: '1.1rem',
            '@media (max-width: 360px)': {
              fontSize: '1rem',
              minWidth: '80%',
            },
            '@media (max-width: 320px)': {
              fontSize: '0.95rem',
              minWidth: '85%',
            },

          }}>
              Back
            </BaseButton>
          </Dialog>
          <BaseButton
            sx={{
              marginTop: '10px', width: '100%', alignSelf: 'center', fontSize: '1.1rem',
              '@media (max-width: 360px)': {
                fontSize: '1rem',
                minWidth: '80%',
              },
              '@media (max-width: 320px)': {
                fontSize: '0.95rem',
                minWidth: '85%',
              },
            }}
            type="submit"
            disabled={response.fetching || !agreeToTerms}
          >
            {response.fetching ? 'Signing Up...' : 'Sign Up'}
          </BaseButton>
          <BaseButton sx={{
            width: '100%',
            marginTop: '10px',
            marginBottom: '20px',
            alignSelf: 'center',
            fontSize: '1.1rem',
            '@media (max-width: 360px)': {
              fontSize: '1rem',
              minWidth: '80%',
            },
            '@media (max-width: 320px)': {
              fontSize: '0.95rem',
              minWidth: '85%',
            },

          }} onClick={() => navigate("/")}>
            Back
          </BaseButton>
        </Box>
        {hasProviders && (
          <Box sx={{ marginTop: '1.5rem', width: '100%' }}>
            <ProviderList callbackURL="/account/provider/callback" />
          </Box>
        )}
      </BaseFormContainer>
    </BackgroundContainer>
  );
}
